import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import {
  ALLOCATION_SETTINGS_HREF,
  PRINTERS_SETTINGS_HREF,
} from '@/lib/settings-links';

/**
 * Legacy → current path map.
 *
 * These paths shipped once and may still be bookmarked, linked from a saved
 * report, or baked into someone's muscle memory. Each renders as a
 * `<Navigate replace>` route inside the protected layout so the old URL lands
 * on the live page instead of a blank screen.
 *
 * Add entries here, not as a scattered `<Route>` in App.tsx.
 *
 * A redirect deliberately has NO entry in `ROUTE_PERMISSIONS`: it is not a page
 * and has nothing to authorize. `canAccessRoute` consults this list and lets
 * the source through; the *target* route does the real check. Gating a
 * redirect instead of its target is how `/bins` ended up denying pickers
 * outright rather than forwarding them to `/slotting`.
 *
 * A `:param` in `to` is filled from the matching `:param` in `from`.
 *
 * A query string on `to` is a DEFAULT: the incoming query is merged over it, so
 * a deep link like `/operational-locations?type=DOCK` keeps its filter and an
 * explicit `?family=` beats the default the row supplies.
 */
export const LEGACY_REDIRECTS: ReadonlyArray<
  readonly [from: string, to: string]
> = [
  // Slotting and Operational Locations were merged into the unified /locations
  // page; each legacy list path lands on its type-family tab.
  ['/bins', '/locations?family=bins'],
  ['/bins/:id', '/locations/:id'],
  ['/slotting', '/locations?family=bins'],
  ['/operational-locations', '/locations?family=operational'],
  ['/operational-locations/:id', '/locations/:id'],
  // Transfers became a flavour of adjustment.
  ['/transfers', '/adjustments?type=RELOCATION'],
  ['/transfers/:id', '/adjustments'],
  // Settings pages moved under /settings/* (C2 PR 4).
  // Warehouse Settings held one rule (cross-warehouse kit putaway); it is now
  // a card on Configuration's Kit Assembly tab.
  ['/warehouse-rules', '/settings/configuration?tab=kit-assembly'],
  ['/settings/warehouse', '/settings/configuration?tab=kit-assembly'],
  ['/allocation-priority-rules', '/settings/priority-rules'],
  ['/allocation-settings', ALLOCATION_SETTINGS_HREF],
  // Printers and Allocation Settings are now parts of the Settings page.
  ['/settings/allocation', ALLOCATION_SETTINGS_HREF],
  ['/settings/printers', PRINTERS_SETTINGS_HREF],
  ['/putaway-rules', '/settings/putaway'],
  ['/pick-split-rules', '/settings/pick-split'],
  ['/channel-rules', '/settings/channel'],
  ['/so-routing-rules', '/settings/so-routing'],
  ['/auto-tag-rules', '/settings/auto-tag'],
  ['/assignment-rules', '/settings/assignment'],
  // The separate mobile shell was retired once MainLayout went responsive
  // (#1426); these keep handheld bookmarks and home-screen shortcuts alive.
  ['/mobile', '/'],
  ['/mobile/picking', '/picking'],
  ['/mobile/picking/:id', '/picking/:id'],
  ['/mobile/inventory', '/inventory'],
  ['/mobile/receiving', '/purchase-orders'],
  ['/mobile/receiving/:id', '/purchase-orders'],
  ['/mobile/putaway', '/putaway'],
  ['/mobile/putaway/:id', '/putaway'],
  // API Keys is mounted at /settings/api-keys; this was a second live mount.
  ['/admin/api-keys', '/settings/api-keys'],
  // The manager dashboard was retired; the SO list absorbed its per-PO actions.
  ['/pick-orders', '/sales-orders'],
];

/**
 * Whether `pathname` is a legacy path that only redirects. Segment-wise match
 * so `:param` patterns work; `pathname` is expected to be already normalised
 * (no query, no hash, no trailing slash).
 */
export function isLegacyRedirect(pathname: string): boolean {
  const actual = pathname.split('/');
  return LEGACY_REDIRECTS.some(([from]) => {
    const pattern = from.split('/');
    if (pattern.length !== actual.length) return false;
    return pattern.every((part, i) =>
      // A :param stands for a non-empty segment. Letting it match '' would
      // grant '/bins/' -- which React Router refuses to match anyway, so the
      // user 404s either way, but the honest answer is still "not a redirect".
      part.startsWith(':') ? actual[i] !== '' : part === actual[i],
    );
  });
}

/**
 * Fills each `:param` in a redirect target with the value the router matched
 * on the source path. A `:param` with no counterpart is left verbatim — no
 * entry needs that today, and a literal `:name` in the URL 404s visibly rather
 * than silently navigating somewhere wrong.
 *
 * `search` is the incoming query string, merged OVER any query the target
 * carries: the target's is a default (`?family=bins`), the incoming one is what
 * the user actually asked for. Without this a deep link loses its filters the
 * moment it goes through a redirect.
 */
export function resolveLegacyTarget(
  to: string,
  params: Readonly<Record<string, string | undefined>>,
  search = '',
): string {
  const filled = to.replace(/:([A-Za-z0-9_]+)/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : encodeURIComponent(value);
  });

  const [path, defaults = ''] = filled.split('?');
  const merged = new URLSearchParams(defaults);
  new URLSearchParams(search).forEach((value, key) => merged.set(key, value));
  const query = merged.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Renders one entry, substituting any `:param` in the target with the value
 * matched from the source path and carrying the incoming query across. Reads
 * both from the router rather than window.location so it respects the basename.
 */
export function LegacyRedirect({ to }: { to: string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={resolveLegacyTarget(to, params, search)} replace />;
}

/**
 * The redirect routes, as JSX. App.tsx renders this and the render test mounts
 * it, so the two cannot disagree about how the table becomes routes.
 *
 * That sharing is the point. When App declared the map itself and the test
 * rebuilt its own copy, swapping `path={from}`/`to={to}` for `path={to}`/
 * `to={from}` inverted all 22 redirects while every test still passed: the
 * test was exercising its own correct copy, and the source-level guard only
 * checked that a map existed. There is now one copy to get wrong.
 */
export function legacyRedirectRoutes() {
  return LEGACY_REDIRECTS.map(([from, to]) => (
    <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
  ));
}
